/**
 * Singleton alert service for managing app-wide alerts
 */

export interface Alert {
  id: string;
  type: 'success' | 'error' | 'info' | 'warning';
  message: string;
  duration?: number; // in milliseconds, default 2000
}

type AlertListener = (alerts: Alert[]) => void;

class AlertService {
  private static instance: AlertService;
  private alerts: Alert[] = [];
  private listeners: AlertListener[] = [];
  private idCounter = 0;

  private constructor() {}

  public static getInstance(): AlertService {
    if (!AlertService.instance) {
      AlertService.instance = new AlertService();
    }
    return AlertService.instance;
  }

  /**
   * Add an alert listener
   */
  public addListener(listener: AlertListener): () => void {
    this.listeners.push(listener);
    // Return unsubscribe function
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  /**
   * Notify all listeners of alert changes
   */
  private notifyListeners(): void {
    this.listeners.forEach(listener => listener([...this.alerts]));
  }

  /**
   * Show a success alert (green panel)
   */
  public success(message: string, duration?: number): void {
    this.show('success', message, duration);
  }

  /**
   * Show an error alert (red panel)
   */
  public error(message: string, duration?: number): void {
    this.show('error', message, duration);
  }

  /**
   * Show an info alert (blue panel)
   */
  public info(message: string, duration?: number): void {
    this.show('info', message, duration);
  }

  /**
   * Show a warning alert (orange panel)
   */
  public warning(message: string, duration?: number): void {
    this.show('warning', message, duration);
  }

  /**
   * Show an alert
   */
  private show(type: Alert['type'], message: string, duration: number = 2000): void {
    const alert: Alert = {
      id: `alert-${++this.idCounter}`,
      type,
      message,
      duration
    };

    this.alerts.push(alert);
    this.notifyListeners();

    // Auto-remove after duration
    setTimeout(() => {
      this.remove(alert.id);
    }, duration);
  }

  /**
   * Remove an alert by ID
   */
  public remove(id: string): void {
    const index = this.alerts.findIndex(n => n.id === id);
    if (index > -1) {
      this.alerts.splice(index, 1);
      this.notifyListeners();
    }
  }

  /**
   * Clear all alerts
   */
  public clear(): void {
    this.alerts = [];
    this.notifyListeners();
  }

  /**
   * Get current alerts
   */
  public getAlerts(): Alert[] {
    return [...this.alerts];
  }
}

// Export both named and default exports for compatibility
const alertService = AlertService.getInstance();
export { alertService as AlertService };
export default alertService;